import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Copy, Check, ShieldCheck, Lock, CheckCircle2, AlertCircle, Clock, Pencil, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, fmtDate, whatsappLink, isIOwe, publicUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const ACK_ERRORS = {
  ack_debt_cooldown: "You just sent a request for this debt. Please wait 10 minutes before sending another.",
  ack_hourly_limit: "You've sent a lot of requests in the last hour. Please try again later.",
  recipient_blocked: "This person has blocked requests for this debt.",
};
const ackErrorText = (err) => ACK_ERRORS[err] || err;

export default function AckSheet({ open, onOpenChange, debt, onRequested, onEditDetails }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const isPlus = user?.subscription_status === "plus";
  const [outsideLimit, setOutsideLimit] = useState(false);

  const ackStatus = debt?.acknowledgement_status || "none";
  const ackInfo = debt?.acknowledgement || {};
  const revised = ackInfo.revised;
  const iOwe = isIOwe(debt);

  // When opening a debt that already has a pending link, reuse it (no new request)
  useEffect(() => {
    if (open && ackStatus === "requested" && ackInfo.path) {
      setLink(publicUrl(ackInfo.path));
    }
  }, [open, ackStatus, ackInfo.path]);

  // Safety: opening this Vaul drawer from a Radix dropdown item can leave the
  // page with document.body { pointer-events: none } after it closes, freezing
  // all navigation until refresh. Always clear it once the drawer is closed.
  useEffect(() => {
    if (!open) {
      setOutsideLimit(false);
      const t = setTimeout(() => { document.body.style.pointerEvents = ""; }, 0);
      return () => clearTimeout(t);
    }
  }, [open]);
  useEffect(() => () => { document.body.style.pointerEvents = ""; }, []);

  const title =
    ackStatus === "acknowledged" ? "Acknowledged"
      : ackStatus === "disputed" ? "Details questioned"
        : ackStatus === "requested" ? "Awaiting acknowledgement"
          : iOwe ? `Ask ${debt?.person_name} to confirm what I owe`
            : `Ask ${debt?.person_name} to acknowledge`;

  const message = link
    ? (iOwe
      ? `Hi ${debt.person_name}, I've recorded the ${fmtNaira(debt.original_amount, debt.currency)} I borrowed from you on Debii.${revised ? " I've updated the details you questioned." : ""} Please review and acknowledge the details here: ${link}`
      : `Hi ${debt.person_name}, I've recorded the ${fmtNaira(debt.original_amount, debt.currency)} I lent you on Debii.${revised ? " I've updated the details you questioned." : ""} Please review and acknowledge the details here: ${link}`)
    : "";

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/debts/${debt.id}/acknowledgement`);
      setLink(publicUrl(data.path));
      onRequested?.();
    } catch (e) {
      const err = apiError(e);
      if (err === "ack_outside_free_limit") setOutsideLimit(true);
      else if (err === "already_acknowledged") toast.error("This debt has already been acknowledged.");
      else toast.error(ackErrorText(err));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(message);
    setCopied(true);
    toast.success("Message copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const sendEmail = async () => {
    const to = emailTo.trim();
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setEmailSending(true);
    try {
      await api.post(`/debts/${debt.id}/acknowledgement/email`, { email: to });
      toast.success(`Request emailed to ${to}`);
      setEmailTo("");
      onRequested?.();
    } catch (e) {
      const err = apiError(e);
      if (err === "ack_outside_free_limit") setOutsideLimit(true);
      else toast.error(ackErrorText(err));
    } finally {
      setEmailSending(false);
    }
  };

  const close = () => {
    setLink(null);
    onOpenChange(false);
  };

  const ShareView = (
    <>
      {ackStatus === "requested" && (
        <div className="flex items-center gap-2 rounded-xl bg-secondary px-3 py-2 text-sm font-medium text-slate-600">
          <Clock className="h-4 w-4" /> Awaiting {debt?.person_name}'s response{revised ? " (updated request)" : ""}
        </div>
      )}
      <div data-testid="ack-link-box" className="rounded-2xl bg-secondary p-4 text-sm break-all text-foreground">
        {link}
      </div>
      <div className="rounded-2xl border border-border p-4 text-sm leading-relaxed text-muted-foreground">
        {message}
      </div>
      <div className="space-y-2">
        <Button
          data-testid="ack-whatsapp-button"
          onClick={() => window.open(whatsappLink(debt.person_phone, message), "_blank")}
          className="w-full h-12 rounded-xl text-base bg-[#25D366] hover:bg-[#20bd5a] text-white"
        >
          <MessageCircle className="h-5 w-5" /> Share via WhatsApp
        </Button>
        <Button data-testid="ack-copy-button" variant="outline" onClick={copy} className="w-full h-11 rounded-xl">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy message
        </Button>
        <div className="pt-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground pb-1.5">
            <Mail className="h-3.5 w-3.5" /> Or email the request directly
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="ack-email-input"
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 300)}
              placeholder={`${debt?.person_name || "Recipient"}'s email (optional)`}
              className="h-11 rounded-xl"
            />
            <Button data-testid="ack-email-send" variant="outline" onClick={sendEmail} disabled={emailSending || !emailTo.trim()} className="h-11 rounded-xl shrink-0">
              {emailSending ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
        <Button variant="ghost" onClick={close} className="w-full h-11 rounded-xl">Done</Button>
      </div>
    </>
  );

  const renderBody = () => {
    if (outsideLimit && !isPlus) {
      return (
        <div className="text-center py-4 space-y-4">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">
            On the Free plan you can send acknowledgement requests for your first 5 active debts. Upgrade to <span className="font-semibold text-foreground">Debii Plus</span> for unlimited active debts. Receiving and responding to requests is always free.
          </p>
          <Button data-testid="ack-upgrade-button" onClick={() => { close(); navigate("/upgrade"); }} className="w-full h-12 rounded-xl">
            Upgrade to Debii Plus
          </Button>
        </div>
      );
    }

    // Already acknowledged — no create button
    if (ackStatus === "acknowledged") {
      return (
        <div data-testid="ack-acknowledged-view" className="text-center py-4 space-y-3">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="h-7 w-7 text-emerald-700" />
          </div>
          <p className="font-display text-lg font-bold text-emerald-800">Acknowledged ✓</p>
          <p className="text-sm text-muted-foreground">
            {debt.person_name} confirmed these details{ackInfo.responded_at ? ` on ${fmtDate(ackInfo.responded_at)}` : ""}.
          </p>
          <Button variant="ghost" onClick={close} className="w-full h-11 rounded-xl">Done</Button>
        </div>
      );
    }

    // Debtor questioned the details — review, edit, then send a fresh (revised) request
    if (ackStatus === "disputed" && !link) {
      return (
        <div data-testid="ack-disputed-view" className="space-y-4">
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 space-y-1.5">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
              <AlertCircle className="h-4 w-4" /> {debt.person_name} questioned these details
            </div>
            {ackInfo.dispute_reason_label && (
              <p className="text-sm text-amber-900">Flagged: {ackInfo.dispute_reason_label}</p>
            )}
            {ackInfo.dispute_comment && (
              <p className="text-sm text-muted-foreground italic">“{ackInfo.dispute_comment}”</p>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Review and correct the details if needed, then send an updated request. {debt.person_name} will see the details were updated after being questioned.
          </p>
          <Button data-testid="ack-edit-button" variant="outline" onClick={() => { close(); onEditDetails?.(); }} className="w-full h-11 rounded-xl">
            <Pencil className="h-4 w-4" /> Edit debt details
          </Button>
          <Button data-testid="ack-resend-button" disabled={loading} onClick={generate} className="w-full h-12 rounded-xl text-base">
            {loading ? "Creating link…" : "Send updated request"}
          </Button>
        </div>
      );
    }

    // Existing/just-created pending link ready to share
    if (link) return ShareView;

    // Pending but no retrievable link yet (legacy record) — fetch/reuse it
    if (ackStatus === "requested") {
      return (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-2xl bg-secondary p-4">
            <Clock className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              You've already asked {debt?.person_name} to acknowledge this. Get the link below to re-share it.
            </p>
          </div>
          <Button data-testid="ack-reshare-button" disabled={loading} onClick={generate} className="w-full h-12 rounded-xl text-base">
            {loading ? "Loading link…" : "Get acknowledgement link"}
          </Button>
        </div>
      );
    }

    // Not requested yet — create a link
    return (
      <>
        <div className="flex items-start gap-3 rounded-2xl bg-secondary p-4">
          <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground">
            We'll create a secure private link. {debt?.person_name} can review the details and confirm — no Debii account needed.
          </p>
        </div>
        <Button data-testid="ack-generate-button" disabled={loading} onClick={generate} className="w-full h-12 rounded-xl text-base">
          {loading ? "Creating link…" : "Create acknowledgement link"}
        </Button>
      </>
    );
  };

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) setLink(null); onOpenChange(o); }}>
      <DrawerContent className="max-w-[30rem] mx-auto">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display text-xl">{title}</DrawerTitle>
        </DrawerHeader>
        <div className="px-5 pb-8 space-y-4 overflow-y-auto overscroll-contain">{renderBody()}</div>
      </DrawerContent>
    </Drawer>
  );
}
